'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';

const certificationInfo: Record<string, string> = {
  FIATA: 'FIATA (Fédération Internationale des Associations de Transitaires et Assimilés) is the world body of the freight forwarding industry, representing around 40,000 freight forwarding and logistics firms globally.',
  SGS: "SGS is the world's leading inspection, verification, testing and certification company. WTO holds SGS certification as a mark of quality and reliability in logistics services.",
  IATA: 'IATA (International Air Transport Association) is the trade association for the world\'s airlines. IATA accreditation allows WTO to issue air waybills and act as an authorized cargo agent.',
  NSBS: 'NSBS (National Suretyship and Bonding System) is a Bulgarian national system providing financial guarantees and suretyship for freight forwarders and customs agents operating in Bulgaria.',
};

const certifications = [
  {
    name: 'FIATA',
    fullName: 'International Federation of Freight Forwarders Associations',
  },
  {
    name: 'SGS',
    fullName: 'Société Générale de Surveillance — Certified Client',
  },
  {
    name: 'IATA',
    fullName: 'International Air Transport Association',
  },
  {
    name: 'NSBS',
    fullName: 'National Standardization Body of Bulgaria',
  },
];

const CertificationsSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setActiveTooltip(null);
      }
    };
    if (activeTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeTooltip]);

  const handleCertClick = useCallback((name: string) => {
    setActiveTooltip((prev) => (prev === name ? null : name));
  }, []);

  return (
    <section id="useful-info" className="relative py-16" ref={sectionRef} style={{ background: '#F4F6F9', borderTop: '1px solid rgba(15,35,64,0.08)' }}>
      {/* Subtle teal line top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(27,107,138,0.4), transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="reveal text-center mb-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-body" style={{ color: '#3A5070' }}>
            <span className="w-6 h-px" style={{ background: 'rgba(15,35,64,0.2)' }} />
            Certifications &amp; Memberships
            <span className="w-6 h-px" style={{ background: 'rgba(15,35,64,0.2)' }} />
          </span>
          <p className="text-xs mt-2 font-body" style={{ color: '#6A85A0' }}>Click any certification to learn more</p>
        </div>

        <div className="reveal grid grid-cols-2 lg:grid-cols-4 gap-4" ref={tooltipRef}>
          {certifications.map((cert, i) => (
            <div key={cert.name} className="relative flex flex-col">
              <button
                onClick={() => handleCertClick(cert.name)}
                className="w-full rounded-2xl p-5 border transition-all duration-300 group text-center cursor-pointer"
                style={{
                  background: activeTooltip === cert.name ? 'rgba(27,107,138,0.1)' : '#FFFFFF',
                  borderColor: activeTooltip === cert.name ? 'rgba(27,107,138,0.4)' : 'rgba(15,35,64,0.12)',
                  transitionDelay: `${i * 60}ms`,
                  boxShadow: activeTooltip === cert.name ? '0 4px 20px rgba(27,107,138,0.15)' : '0 1px 8px rgba(15,35,64,0.07)',
                }}
              >
                <p className="font-display text-2xl lg:text-3xl font-medium mb-2 transition-colors duration-300" style={{ color: '#0F2340' }}>
                  {cert.name}
                </p>
                <p className="text-xs font-body leading-relaxed" style={{ color: '#6A85A0' }}>
                  {cert.fullName}
                </p>
                <div className="mt-3 w-6 h-px mx-auto group-hover:w-10 transition-all duration-300" style={{ background: 'rgba(27,107,138,0.5)' }} />
                <p className="text-xs mt-2 font-body" style={{ color: 'rgba(27,107,138,0.7)' }}>
                  {activeTooltip === cert.name ? '▲ Close' : '▼ Info'}
                </p>
              </button>

              {/* Inline info panel */}
              {activeTooltip === cert.name && (
                <div
                  className="mt-2 rounded-xl p-4"
                  style={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(27,107,138,0.3)',
                    boxShadow: '0 8px 32px rgba(15,35,64,0.12)',
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-display text-sm font-semibold" style={{ color: '#1B6B8A' }}>{cert.name}</p>
                    <button
                      onClick={() => setActiveTooltip(null)}
                      className="text-xs shrink-0 mt-0.5"
                      style={{ color: '#6A85A0' }}
                    >
                      ✕
                    </button>
                  </div>
                  <p className="text-xs font-body leading-relaxed" style={{ color: '#3A5070' }}>
                    {certificationInfo[cert.name]}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="reveal mt-12 rounded-2xl p-6 lg:p-8 border flex flex-col lg:flex-row items-center justify-between gap-6" style={{ background: 'linear-gradient(135deg, #0F2340 0%, #1A3558 100%)', borderColor: 'rgba(27,107,138,0.3)' }}>
          <div>
            <h3 className="font-display text-xl lg:text-2xl mb-1" style={{ color: '#FFFFFF' }}>Ready to ship with WTO?</h3>
            <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Get a quote today — we&apos;ll respond within <span style={{ color: '#7EC8DC', fontWeight: 600 }}>2 hours</span>, Monday–Friday.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Link
              href="/transport-quote"
              className="px-5 py-2.5 rounded-full text-sm font-semibold font-body transition-colors duration-200 hover:opacity-90"
              style={{ background: '#1A5FAB', color: '#ffffff' }}
            >
              Get a Quote — We Respond in 2 Hours
            </Link>
            <Link
              href="/contacts"
              className="px-5 py-2.5 rounded-full text-sm font-medium font-body transition-all duration-200"
              style={{ border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationsSection;